import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Coffee, Phone, PhoneCall, PhoneOff, Volume2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { SoftphoneStatus, useSoftphone } from '../app/softphone';
import { errorMessage, get, put } from '../lib/api';
import { formatPhone, since } from '../lib/format';
import { AgentStatusView, WaitingCall } from '../lib/types';
import { toast } from './toasts';
import { ExtensionPicker } from './ExtensionPicker';
import { Button, Select } from './ui';

const STATUS: Record<SoftphoneStatus, { label: string; dot: string }> = {
  off: { label: 'Desligado', dot: 'bg-border' },
  not_configured: { label: 'Telefone não configurado no servidor', dot: 'bg-border' },
  no_credentials: { label: 'Sem ramal cadastrado para você', dot: 'bg-border' },
  connecting: { label: 'Conectando ao PBX…', dot: 'bg-warn animate-pulse' },
  registered: { label: 'Pronto para receber ligações', dot: 'bg-ok' },
  standby: { label: 'Ramal ativo em outra aba', dot: 'bg-fg-soft' },
  elsewhere: { label: 'Ramal ativo em outro navegador', dot: 'bg-fg-soft' },
  error: { label: 'Erro no ramal', dot: 'bg-danger' },
};

const PAUSE_REASONS = ['Almoço', 'Intervalo', 'Reunião', 'Retorno de ligações', 'Outro'];

/** Telefone no topo: situação do ramal, disponível/pausa na fila, clientes esperando, discador e áudio. */
export function SoftphoneButton() {
  const sp = useSoftphone();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [number, setNumber] = useState('');
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [chosen, setChosen] = useState(sp.engine.getDevices());
  const box = useRef<HTMLDivElement>(null);
  const s = STATUS[sp.status];
  // Sem ramal cadastrado nem servidor configurado: o botão nem aparece.
  const enabled = sp.status !== 'not_configured' && sp.status !== 'no_credentials' && sp.status !== 'off';
  const me = useQuery({ queryKey: ['telephony', 'me'], queryFn: () => get<AgentStatusView>('/telephony/me/status'), enabled });
  const waiting = useQuery({ queryKey: ['calls', 'waiting'], queryFn: () => get<WaitingCall[]>('/telephony/queue/waiting'), enabled, refetchInterval: 30_000 }).data ?? [];
  const paused = me.data?.status === 'paused';

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    window.addEventListener('mousedown', onDown);
    void navigator.mediaDevices?.enumerateDevices().then(setDevices);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  if (!enabled) return null;

  const mics = devices.filter((d) => d.kind === 'audioinput');
  const speakers = devices.filter((d) => d.kind === 'audiooutput');
  const needsPermission = devices.length > 0 && devices.every((d) => !d.label);
  const choose = (patch: Partial<typeof chosen>) => {
    const next = { ...chosen, ...patch };
    setChosen(next);
    sp.engine.setDevices(next);
  };
  const askPermission = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true }).catch(() => null);
    stream?.getTracks().forEach((t) => t.stop());
    setDevices(await navigator.mediaDevices.enumerateDevices());
  };
  const dial = () => {
    sp.engine.call(number);
    setNumber('');
    setOpen(false);
  };
  const setAvailability = async (status: 'available' | 'paused', reason?: string) => {
    try {
      await put('/telephony/me/status', { status, reason });
      void qc.invalidateQueries({ queryKey: ['telephony'] });
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };

  return (
    <div className="relative flex items-center" ref={box}>
      <button className="relative rounded p-1.5 text-fg-soft hover:bg-muted" onClick={() => setOpen((o) => !o)} title={`Telefone: ${paused ? 'em pausa' : s.label}`}>
        {sp.call ? <PhoneCall className="size-4 text-ok" /> : sp.status === 'error' ? <PhoneOff className="size-4" /> : <Phone className="size-4" />}
        <span className={clsx('absolute right-0.5 bottom-0.5 size-2 rounded-full ring-2 ring-panel', paused && sp.status === 'registered' ? 'bg-warn' : s.dot)} />
        {waiting.length > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-warn px-1 text-[10px] leading-none font-semibold text-white"
            title="Clientes esperando na fila de ligações"
          >
            {waiting.length}
          </span>
        )}
      </button>
      {paused && (
        <button className="ml-0.5 rounded bg-warn/15 px-1.5 py-0.5 text-[11px] font-medium text-warn" onClick={() => void setAvailability('available')} title="Em pausa: clique para ficar disponível">
          Pausa
        </button>
      )}
      {open && (
        <div className="fixed inset-x-2 top-14 z-30 max-h-[80dvh] overflow-y-auto rounded-md md:absolute md:inset-x-auto md:top-full md:right-0 md:mt-1 md:max-h-none md:w-80 md:overflow-visible border border-border bg-panel p-3 text-sm shadow-lg">
          <div className="mb-2 flex items-center gap-2">
            <span className={clsx('size-2 rounded-full', s.dot)} />
            <span className="font-medium">Ramal {sp.extension}</span>
            <span className="text-xs text-fg-soft">· {s.label}</span>
          </div>
          {sp.error && <p className="mb-2 text-xs text-danger">{sp.error}</p>}
          {(sp.status === 'standby' || sp.status === 'elsewhere') && (
            <div className="mb-3 rounded-md bg-muted p-2">
              <p className="mb-2 text-xs text-fg-soft">
                {sp.status === 'standby'
                  ? 'Outra aba desta plataforma está com o ramal. Feche-a ou traga o telefone para esta.'
                  : 'O ramal está em outro navegador ou computador. Se ele ficou aberto e esquecido, traga o telefone para cá: o outro sai do ramal (e uma ligação em andamento nele cai).'}
              </p>
              <Button size="sm" variant="primary" onClick={() => sp.engine.takeOver()}>
                <PhoneCall className="size-3.5" /> Usar o telefone aqui
              </Button>
            </div>
          )}

          {me.data?.queue && (
            <div className="mb-3 rounded-md bg-muted p-2">
              <div className="mb-1.5 flex items-center justify-between gap-2 text-xs">
                <span className="font-medium">Fila de ligações {me.data.queue}</span>
                <span className={paused ? 'text-warn' : 'text-ok'}>
                  {paused ? `Em pausa: ${me.data.reason ?? ''}` : 'Disponível'}
                  {me.data.since && <> · há {since(me.data.since)}</>}
                </span>
              </div>
              {paused ? (
                <Button size="sm" variant="success" className="w-full" onClick={() => void setAvailability('available')}>
                  Ficar disponível
                </Button>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {PAUSE_REASONS.map((r) => (
                    <Button key={r} size="sm" onClick={() => void setAvailability('paused', r)} title="Pausar: a fila não toca para você">
                      <Coffee className="size-3" /> {r}
                    </Button>
                  ))}
                </div>
              )}
              {waiting.length > 0 && (
                <div className="mt-2 border-t border-border pt-1.5">
                  <div className="mb-1 text-[11px] font-semibold tracking-wide text-fg-soft uppercase">Esperando na linha ({waiting.length})</div>
                  {waiting.slice(0, 5).map((w) => (
                    <div key={w.id} className="flex justify-between text-xs">
                      <span>{w.phone ? formatPhone(w.phone) : (w.fromNumber ?? 'número oculto')}</span>
                      <span className="text-fg-soft">há {since(w.queuedAt)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {sp.status === 'registered' && !sp.call && (
            <form
              className="mb-3 flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                dial();
              }}
            >
              <ExtensionPicker value={number} onChange={setNumber} placeholder="Nome, ramal ou número" excludeExtension={sp.extension} autoFocus />
              <Button type="submit" variant="primary" disabled={!number.trim()}>
                <Phone className="size-4" /> Ligar
              </Button>
            </form>
          )}

          <div className="space-y-2 border-t border-border pt-2">
            {needsPermission && (
              <Button size="sm" onClick={() => void askPermission()}>
                Permitir o microfone para listar os dispositivos
              </Button>
            )}
            <label className="block text-xs text-fg-soft">
              Microfone
              <Select value={chosen.micId} onChange={(e) => choose({ micId: e.target.value })}>
                <option value="">Padrão do sistema</option>
                {mics.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || 'Microfone'}
                  </option>
                ))}
              </Select>
            </label>
            {speakers.length > 0 && (
              <label className="block text-xs text-fg-soft">
                Alto-falante / fone
                <Select value={chosen.speakerId} onChange={(e) => choose({ speakerId: e.target.value })}>
                  <option value="">Padrão do sistema</option>
                  {speakers.map((d) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label || 'Alto-falante'}
                    </option>
                  ))}
                </Select>
              </label>
            )}
            <Button size="sm" onClick={() => void sp.engine.testSpeaker()}>
              <Volume2 className="size-3.5" /> Testar o toque
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
